import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/api";

const authHeader = () => ({ "X-Access-Token": localStorage.getItem("guardia_bp_token") || "" });
const blobCache = new Map();

async function fetchClip(text) {
  if (!localStorage.getItem("guardia_bp_token")) throw new Error("no token");
  if (blobCache.has(text)) return blobCache.get(text);
  const p = api
    .post("/blueprint/voice", { text }, { headers: authHeader(), responseType: "blob" })
    .then((r) => URL.createObjectURL(r.data))
    .catch((e) => { blobCache.delete(text); throw e; });
  blobCache.set(text, p);
  return p;
}

const L = {
  it: {
    warmup: (n) => `Si comincia. ${n}. Sciogli quelle spalle, ragazzo.`,
    round: (n, c) => `Forza! ${n}. ${c} Mani alte, mento basso. Vai!`,
    rest: () => "Tempo! Respira dal naso. Recupero.",
    drill: (n, c) => `${n}. ${c} Pulito, niente scorciatoie.`,
    finisher: (n, c) => `Ultimo sforzo! ${n}. ${c} Svuota il serbatoio!`,
    cooldown: () => "Defaticamento. Hai lavorato bene, ora respira.",
    ten: "Dieci secondi! Spingi!",
    count: ["Uno", "Due", "Tre"],
    done: "Ottimo lavoro, campione. Per oggi basta.",
  },
  en: {
    warmup: (n) => `Here we go. ${n}. Loosen those shoulders, kid.`,
    round: (n, c) => `Come on! ${n}. ${c} Hands up, chin down. Go!`,
    rest: () => "Time! Breathe through your nose. Rest.",
    drill: (n, c) => `${n}. ${c} Clean, no shortcuts.`,
    finisher: (n, c) => `Last push! ${n}. ${c} Empty the tank!`,
    cooldown: () => "Cool-down. Good work, now breathe.",
    ten: "Ten seconds! Push!",
    count: ["One", "Two", "Three"],
    done: "Great work, champ. That's enough for today.",
  },
};

const clean = (s) => s.replace(/·/g, ",").replace(/[–—→]/g, ", ").replace(/\s+/g, " ").trim();

export function blockLine(block, lang) {
  const l = L[lang] || L.it;
  const name = clean(block.name[lang] || block.name.it);
  const cue = block.cues?.[0] ? `${clean(block.cues[0][lang] || block.cues[0].it)}.` : "";
  return clean(l[block.kind](name, cue)).slice(0, 220);
}

export const coachPhrases = (lang) => L[lang] || L.it;

export function useCoachVoice(lines) {
  const [enabled, setEnabled] = useState(() => localStorage.getItem("guardia_coach_voice") !== "off");
  const [ready, setReady] = useState(false);
  const audio = useRef(null);

  useEffect(() => { localStorage.setItem("guardia_coach_voice", enabled ? "on" : "off"); }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    setReady(false);
    (async () => {
      const queue = [...new Set(lines)];
      const worker = async () => { while (queue.length) { try { await fetchClip(queue.shift()); } catch (e) {} } };
      await Promise.all([worker(), worker(), worker()]);
      if (alive) setReady(true);
    })();
    return () => { alive = false; };
  }, [enabled, lines.join("|")]); // eslint-disable-line

  const say = useCallback(async (text) => {
    if (!enabled || !text) return;
    try {
      const url = await fetchClip(text);
      if (audio.current) audio.current.pause();
      audio.current = new Audio(url);
      await audio.current.play();
    } catch (e) {}
  }, [enabled]);

  const stop = useCallback(() => { if (audio.current) audio.current.pause(); }, []);

  return { enabled, setEnabled, ready, say, stop };
}
